import {
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  FormControlLabel,
  FormHelperText,
  Grid,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Refresh } from "@material-ui/icons";
import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import DropdownComponent from "../../Components/Dropdown/DropdownComponent";
import DeleteClientModal from "../../Components/Modal/DeleteClientModal";
import Signifier from "../../Components/Signifier";
import { StyledTextField } from "../../Components/TextField/TextField";
import { ClientType, ClientTypeLookup, ClientTypeReverseLookup } from "../../Enums/ClientType";
import { useAsyncDebouncedHandler } from "../../Helpers/debounced";
import { useBaseClientQuery } from "../../Queries/BaseClientManagementQueries";
import {
  defaultHereDotComAccount,
  useCareersClientQuery,
  useClientAttributionQuery,
  useEditClientMutation,
  useHereDotComAccountsQuery,
  useIndexClientMutation,
  useSaveClientAttributionMutation,
} from "../../Queries/ClientManagementQueries";
import {
  Client,
  ClientAttributionConfiguration,
  EditClient,
  HereDotcomAccount,
} from "../../Types/ClientManagement";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "90%",
    marginBottom: "50px",
  },
  header: {
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      marginTop: "10px",
    },
    display: "flex",
  },
  card: {
    width: "98%",
  },
  descBlock: {
    display: "flex",
  },
  descLabel: {
    margin: "0px",
    fontWeight: 700,
    marginBottom: "10px",
  },
  descValue: {
    margin: "0px",
    marginLeft: "5px",
    marginRight: "20px",
  },
  deleteButton: {
    [theme.breakpoints.down("sm")]: {
      marginBottom: "50px",
      width: "98%",
    },
    marginTop: "20px",
    width: "200px",
  },
  sensitiveButton: {
    [theme.breakpoints.down("sm")]: {
      maxWidth: "none",
      width: "100%",
    },
    marginTop: "20px",
    width: "200px",
  },
  textField: {
    "& .Mui-error": {
      color: "rgba(255,0,0,.7)",
    },
    "& .MuiFormHelperText-root": {
      color: "rgba(255,0,0,.7)",
    },
    "& .MuiInput-underline.Mui-error:after": {
      borderBottomColor: "rgba(255,0,0,.7) !important",
    },
  },
}));

export default function ClientDetailsPage() {
  const classes = useStyles();
  const { state } = useLocation<Client>();
  const history = useHistory();

  const { data: clientData, isFetched: clientDataFetched } = useCareersClientQuery(state.id ?? "");
  const { data: baseClient } = useBaseClientQuery(state.centralClientId ?? "");
  const [clientName, setClientName] = useState(state.name ?? "");
  const [hostName, setHostName] = useState(state.hostname ?? "");
  const [clientType, setClientType] = useState<string>(
    ClientTypeReverseLookup[(state.type ?? ClientType.Active) as ClientType],
  );
  const [companyWebsite, setCompanyWebsite] = useState(state.companyWebsite ?? "");
  const [themeName, setThemeName] = useState(state.themeName ?? "");
  const [talentegyTrackingId, setTalentegyTrackingId] = useState(state.talentegyTrackingId ?? "");
  const [talentNetworkEmbedScript, setTalentNetworkEmbedScript] = useState(
    state.talentNetworkEmbedScript ?? "",
  );

  const {
    data: { hereDotComBetaValues = [], hereDotComProdValues = [] } = {},
    isFetched: isHereDotComFetched,
  } = useHereDotComAccountsQuery();
  const [hereDotComBetaAccount, setHereDotComBetaAccount] =
    useState<HereDotcomAccount>(defaultHereDotComAccount);
  const [hereDotComProdAccount, setHereDotComProdAccount] =
    useState<HereDotcomAccount>(defaultHereDotComAccount);

  const [ssl, setSsl] = useState(state.isSsl ?? false);
  const [gdpr, setGdpr] = useState(state.isGdpr ?? false);
  const [advancedCookies, setAdvancedCookies] = useState(state.isAdvancedCookie ?? false);
  const [proxySort, setProxySort] = useState(state.hasProxySort ?? false);
  const [latestJobs, setLatestJobs] = useState(state.sendLatestJobsTalentNetwork ?? false);
  const [brandAsOrg, setBrandAsOrg] = useState(state.useBrandAsOrg ?? false);
  const [isNotActivate, setIsNotActivate] = useState(state.isNotActivateClient ?? false);

  const { data: clientAttribution, isFetched: isAttributionFetched } = useClientAttributionQuery(
    state.id ?? "",
  );
  const { mutateAsync: saveAttribution, isLoading: isLoadingSaveAttribution } =
    useSaveClientAttributionMutation();
  const [isDisplayed, setIsDisplayed] = useState(false);
  const [isInverted, setIsInverted] = useState(false);
  const [imageName, setImageName] = useState("");

  const { mutateAsync: editClient, isLoading: isLoadingEditClient } = useEditClientMutation();
  const [showInputError, setShowInputError] = useState(false);

  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteProcessing, setDeleteProcessing] = useState(false);
  const [successfulDelete, setSuccessfulDelete] = useState(false);

  const [showSignifier, setShowSignifier] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [showError, setShowError] = useState(false);

  async function saveClientJson() {
    if (
      clientName === "" ||
      hostName === "" ||
      companyWebsite === "" ||
      (talentNetworkEmbedScript && talentNetworkEmbedScript.length > 1000)
    ) {
      setShowInputError(true);
      return;
    }

    const editedClient = {
      name: clientName,
      hostname: hostName,
      type: ClientTypeLookup[clientType],
      companyWebsite,
      themeName,
      talentegyTrackingId,
      talentNetworkEmbedScript,
      isSsl: ssl,
      isGdpr: gdpr,
      isAdvancedCookie: advancedCookies,
      hasProxySort: proxySort,
      sendLatestJobsTalentNetwork: latestJobs,
      useBrandAsOrg: brandAsOrg,
      hereDotComBetaAccountId: hereDotComBetaAccount.id === "None" ? null : hereDotComBetaAccount.id,
      hereDotComProductionAccountId: hereDotComProdAccount.id === "None" ? null : hereDotComProdAccount.id,
      isNotActivateClient: isNotActivate,
    } as EditClient;

    if (clientData?.id) {
      await editClient(
        { clientId: clientData.id, editedClient },
        {
          onSuccess: (success) => {
            handleApiResponse(success, "Client Saved", "Error Saving Client Information");
          },
        },
      );
    }
  }

  async function submitAttribution() {
    if (imageName && imageName.length > 50) {
      setShowInputError(true);
      return;
    }

    if (clientData?.id) {
      const attribution = {
        clientId: clientData.id,
        isDisplayed,
        isInverted,
        imageName,
      } as ClientAttributionConfiguration;

      await saveAttribution(attribution, {
        onSuccess: (savedConfig) => {
          const success = savedConfig?.clientId !== undefined && savedConfig?.clientId !== "";
          handleApiResponse(success, "Attribution Saved", "Error Saving Attribution");
        },
      });
    }
  }

  const handleApiResponse = (success: boolean, successMessage: string, errorMessage: string) => {
    setShowInputError(false);
    setShowSignifier(true);
    if (success) {
      setSuccessMessage(successMessage);
      setShowError(false);
      setErrorMessage(undefined);
    } else {
      setSuccessMessage("");
      setShowError(true);
      setErrorMessage(errorMessage);
    }
  };

  function setHereDotComId(id: string, isBeta: boolean) {
    let account = {} as HereDotcomAccount;
    if (isBeta) {
      if (!id) {
        setHereDotComBetaAccount(hereDotComBetaValues[0]);
      } else {
        account = hereDotComBetaValues.find((account) => account.id === id) ?? account;
        setHereDotComBetaAccount(account);
      }
    } else {
      if (!id) {
        setHereDotComProdAccount(hereDotComProdValues[0]);
      } else {
        account = hereDotComProdValues.find((account) => account.id === id) ?? account;
        setHereDotComProdAccount(account);
      }
    }
  }

  const { mutateAsync: indexClientJobs, isLoading: isIndexingClientJobs, isError } = useIndexClientMutation();

  const reindexClientJobs = useAsyncDebouncedHandler(async (clientId: string) => {
    if (isLoadingEditClient || isLoadingSaveAttribution || isIndexingClientJobs) {
      return;
    }
    setShowSignifier(true);
    setSuccessMessage("Reindexing Client Jobs...");
    const message = await indexClientJobs(clientId);
    if (!isError && message) {
      setSuccessMessage("Client Jobs Indexing Started, this may take a few minutes.");
      setShowError(false);
      setErrorMessage(undefined);
    } else {
      setShowError(true);
      setErrorMessage("Error Reindexing Client Jobs");
    }
    setTimeout(() => {
      setShowSignifier(false);
    }, 3000);
  }, 300);

  useEffect(() => {
    if (!state.id) {
      history.replace("/careersclientmanagement");
    }
  }, [state.id, history]);

  useEffect(() => {
    if (isAttributionFetched && clientAttribution) {
      setIsDisplayed(clientAttribution.isDisplayed);
      setIsInverted(clientAttribution.isInverted);
      setImageName(clientAttribution.imageName);
    }
  }, [clientAttribution, isAttributionFetched]);

  useEffect(() => {
    if (isHereDotComFetched && hereDotComBetaValues.length > 0 && hereDotComProdValues.length > 0) {
      setHereDotComId(state.hereDotComBetaAccountId ?? "None", true);
      setHereDotComId(state.hereDotComProductionAccountId ?? "None", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isHereDotComFetched,
    hereDotComBetaValues,
    hereDotComProdValues,
    state.hereDotComBetaAccountId,
    state.hereDotComProductionAccountId,
  ]);

  useEffect(() => {
    // State is based on history location state, update the state with the fetched client
    // data, if it exists, without having to return to the full client list
    if (clientDataFetched && clientData) {
      setClientName(clientData.name);
      setHostName(clientData.hostname);
      setClientType(ClientTypeReverseLookup[clientData.type as ClientType]);
      setCompanyWebsite(clientData.companyWebsite);
      setThemeName(clientData.themeName);
      setTalentegyTrackingId(clientData.talentegyTrackingId);
      setTalentNetworkEmbedScript(clientData.talentNetworkEmbedScript);
      setSsl(clientData.isSsl);
      setGdpr(clientData.isGdpr);
      setAdvancedCookies(clientData.isAdvancedCookie);
      setProxySort(clientData.hasProxySort);
      setLatestJobs(clientData.sendLatestJobsTalentNetwork);
      setBrandAsOrg(clientData.useBrandAsOrg);
      setIsNotActivate(clientData.isNotActivateClient);
      setHereDotComId(clientData.hereDotComBetaAccountId ?? state.hereDotComBetaAccountId ?? "None", true);
      setHereDotComId(
        clientData.hereDotComProductionAccountId ?? state.hereDotComProductionAccountId ?? "None",
        false,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientData, clientDataFetched, state.hereDotComBetaAccountId, state.hereDotComProductionAccountId]);

  return (
    <>
      <Signifier
        showSignifier={showSignifier}
        setShowSignifier={setShowSignifier}
        message={successMessage}
        error={showError}
        errorMessage={errorMessage}
      />
      <div className={classes.header}>
        <div className={classes.descBlock}>
          <p className={classes.descLabel}>SHORTNAME: </p>
          <p className={classes.descValue}>{clientData?.shortName}</p>
        </div>
        <div className={classes.descBlock}>
          <p className={classes.descLabel}>DATE CREATED: </p>
          <p className={classes.descValue}>{clientData?.dateCreated}</p>
        </div>
        <div className={classes.descBlock}>
          <p className={classes.descLabel}>BASE CLIENT: </p>
          <p className={classes.descValue} data-test={"careersclientdetails-baseclientname"}>
            {baseClient?.clientName ?? ""}
          </p>
        </div>
      </div>

      <Grid>
        <Card className={classes.card}>
          <CardContent>
            <Grid container spacing={1}>
              <Grid xs={12} sm={4} item>
                <StyledTextField
                  InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                  placeholder="Client Name"
                  label="Client Name"
                  variant="standard"
                  fullWidth
                  required
                  error={showInputError && clientName === ""}
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                />
                {showInputError && clientName === "" ? (
                  <FormHelperText style={{ color: "rgba(255,0,0,.7)" }}>Client Name Required</FormHelperText>
                ) : null}
              </Grid>
              <Grid xs={12} sm={4} item>
                <StyledTextField
                  InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                  placeholder="Hostname"
                  label="Hostname"
                  variant="standard"
                  fullWidth
                  required
                  error={showInputError && hostName === ""}
                  value={hostName}
                  onChange={(e) => setHostName(e.target.value)}
                />
                {showInputError && hostName === "" ? (
                  <FormHelperText style={{ color: "rgba(255,0,0,.7)" }}>Host Name Required</FormHelperText>
                ) : null}
              </Grid>
              <Grid xs={12} sm={4} item>
                <StyledTextField
                  InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                  placeholder="Company Website"
                  label="Company Website"
                  variant="standard"
                  fullWidth
                  required
                  error={showInputError && hostName === ""}
                  value={companyWebsite}
                  onChange={(e) => setCompanyWebsite(e.target.value)}
                />
                {showInputError && companyWebsite === "" ? (
                  <FormHelperText style={{ color: "rgba(255,0,0,.7)" }}>
                    Company Website Required
                  </FormHelperText>
                ) : null}
              </Grid>
            </Grid>
            <Grid container spacing={1}>
              <Grid xs={12} sm={4} item>
                <StyledTextField
                  InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                  placeholder="Theme Name"
                  label="Theme Name"
                  variant="standard"
                  fullWidth
                  value={themeName}
                  onChange={(e) => setThemeName(e.target.value)}
                />
              </Grid>
              <Grid xs={12} sm={4} item>
                <StyledTextField
                  InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                  placeholder="Talentegy Tracking Code"
                  label="Talentegy Tracking Code"
                  variant="standard"
                  fullWidth
                  value={talentegyTrackingId}
                  onChange={(e) => setTalentegyTrackingId(e.target.value)}
                />
              </Grid>
              <Grid xs={12} sm={4} item>
                <StyledTextField
                  InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                  placeholder="Embed Script"
                  label="CRM Talent Network Embed Script"
                  value={talentNetworkEmbedScript}
                  fullWidth
                  variant="standard"
                  onChange={(e) => setTalentNetworkEmbedScript(e.target.value)}
                  data-test="edit-base-client-tnembed-script"
                />
                {showInputError && talentNetworkEmbedScript && talentNetworkEmbedScript.length > 1000 && (
                  <FormHelperText style={{ color: "rgba(255,0,0,.7)" }}>
                    Embed script cannot be longer than 1000 characters
                  </FormHelperText>
                )}
              </Grid>
              <Grid xs={12} sm={4} item>
                <DropdownComponent
                  currValue={hereDotComBetaAccount.id ?? ""}
                  items={hereDotComBetaValues}
                  handleChange={(id: string) => setHereDotComId(id, true)}
                  title="Here.com Beta Account"
                  property="name"
                  multi={undefined}
                  error={false}
                  itemIdSelector={(item: HereDotcomAccount) => item.id}
                />
              </Grid>
              <Grid xs={12} sm={4} item>
                <DropdownComponent
                  currValue={hereDotComProdAccount.id ?? ""}
                  items={hereDotComProdValues}
                  handleChange={(id: string) => setHereDotComId(id, false)}
                  title="Here.com Production Account"
                  property="name"
                  multi={undefined}
                  error={false}
                  itemIdSelector={(item: HereDotcomAccount) => item.id}
                />
              </Grid>
              <Grid xs={12} sm={4} item>
                <DropdownComponent
                  currValue={clientType}
                  items={[
                    ClientTypeReverseLookup[ClientType.Active],
                    ClientTypeReverseLookup[ClientType.Inactive],
                    ClientTypeReverseLookup[ClientType.Test],
                  ]}
                  handleChange={(item: string) => setClientType(item)}
                  title="Client Type"
                  error={false}
                  itemIdSelector={undefined}
                  multi={undefined}
                  property={undefined}
                />
              </Grid>
            </Grid>
            <Grid container spacing={1} style={{ marginTop: "20px" }}>
              <Grid xs={12} sm={4} item>
                <FormControlLabel
                  label="SSL"
                  control={
                    <Checkbox color="primary" checked={ssl} onChange={(e) => setSsl(e.target.checked)} />
                  }
                />
              </Grid>
              <Grid xs={12} sm={4} item>
                <FormControlLabel
                  label="GDPR"
                  control={
                    <Checkbox color="primary" checked={gdpr} onChange={(e) => setGdpr(e.target.checked)} />
                  }
                />
              </Grid>
              <Grid xs={12} sm={4} item>
                <FormControlLabel
                  label="Advanced Cookies"
                  control={
                    <Checkbox
                      color="primary"
                      checked={advancedCookies}
                      onChange={(e) => setAdvancedCookies(e.target.checked)}
                    />
                  }
                />
              </Grid>
              <Grid xs={12} sm={4} item>
                <FormControlLabel
                  label="Results Proximity Sort"
                  control={
                    <Checkbox
                      color="primary"
                      checked={proxySort}
                      onChange={(e) => setProxySort(e.target.checked)}
                    />
                  }
                />
              </Grid>
              <Grid xs={12} sm={4} item>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={latestJobs}
                      onChange={(e) => setLatestJobs(e.target.checked)}
                    />
                  }
                  label="Only Send Latest Job Alerts"
                />
              </Grid>
              <Grid xs={12} sm={4} item>
                <FormControlLabel
                  label="Use Brand as Organization Name (Google Jobs object)"
                  control={
                    <Checkbox
                      color="primary"
                      checked={brandAsOrg}
                      onChange={(e) => setBrandAsOrg(e.target.checked)}
                    />
                  }
                />
              </Grid>
              <Grid xs={12} sm={4} item>
                <FormControlLabel
                  label="Not An Activate Client"
                  control={
                    <Checkbox
                      color="primary"
                      checked={isNotActivate}
                      onChange={(e) => setIsNotActivate(e.target.checked)}
                    />
                  }
                />
              </Grid>
            </Grid>

            <Grid
              xs={12}
              sm={12}
              item
              container
              style={{
                justifyContent: "flex-end",
                display: "flex",
                alignItems: "center",
                gap: "20px",
                paddingTop: "20px",
              }}
            >
              <Button
                variant="outlined"
                color="default"
                disabled={!clientData?.id}
                onClick={() => (clientData && clientData.id ? reindexClientJobs(clientData.id) : undefined)}
                endIcon={<Refresh />}
              >
                Refresh Jobs Index
              </Button>
              <Button
                className={classes.sensitiveButton}
                style={{ marginTop: 0 }}
                type="submit"
                variant="contained"
                color="primary"
                onClick={() => saveClientJson()}
              >
                Save Information
              </Button>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
      <Grid>
        <Card className={classes.card} style={{ marginTop: "20px" }}>
          <CardContent>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={12}>
                <FormHelperText>Client Attribution</FormHelperText>
              </Grid>
              <Grid xs={12} sm={4} item>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={isDisplayed}
                      onChange={() => setIsDisplayed(!isDisplayed)}
                    />
                  }
                  label="Display Attribution"
                />
              </Grid>
              <Grid xs={12} sm={4} item>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={isInverted}
                      onChange={() => setIsInverted(!isInverted)}
                    />
                  }
                  label="Invert Color"
                />
              </Grid>
              <Grid xs={12} sm={4} item>
                <StyledTextField
                  InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                  placeholder="Activate-Logo.svg"
                  label="Attribution File Name (optional)"
                  variant="standard"
                  fullWidth
                  value={imageName}
                  onChange={(e) => setImageName(e.target.value)}
                  error={Boolean(showInputError && imageName && imageName.length > 50)}
                />
                {showInputError && imageName && imageName.length > 50 && (
                  <FormHelperText style={{ color: "rgba(255,0,0,.7)" }}>
                    Image Name cannot be longer than 50 characters
                  </FormHelperText>
                )}
                <FormHelperText>
                  File must be located in the Activate CDN folder:{" "}
                  <code
                    style={{ backgroundColor: "rgba(0,0,0,0.05)", borderRadius: "3px", padding: "2px 3px" }}
                  >
                    Shared/Images/
                  </code>
                </FormHelperText>
              </Grid>
              <Grid xs={12} sm={12} container item style={{ justifyContent: "flex-end" }}>
                <Button
                  style={{ marginRight: "8px" }}
                  className={classes.sensitiveButton}
                  type="submit"
                  variant="contained"
                  color="primary"
                  onClick={() => submitAttribution()}
                >
                  Save Attribution
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
      <Grid xs={12} sm={12} container item>
        <Button
          className={classes.deleteButton}
          type="submit"
          variant="contained"
          color="secondary"
          onClick={() => setDeleteModal(true)}
        >
          Delete Client
        </Button>
      </Grid>

      <Dialog
        open={deleteModal}
        onClose={(event, reason) => {
          if (!successfulDelete && !deleteProcessing && reason === "backdropClick") {
            setDeleteModal(false);
          } else if (successfulDelete && reason === "backdropClick") {
            return;
          }
        }}
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
      >
        <DeleteClientModal
          clientId={state.id}
          clientName={state.name}
          deleteProcessing={deleteProcessing}
          setDeleteProcessing={setDeleteProcessing}
          successfulDelete={successfulDelete}
          setSuccessfulDelete={setSuccessfulDelete}
          closeModal={() => setDeleteModal(false)}
        />
      </Dialog>
    </>
  );
}
